import { ops } from 'conditions/types'
import { checkOp } from 'conditions/utils/utils'
import lodash from 'lodash'
import { handleAddComponent as addComponent } from 'projectSections/sections/design/util/handleAddComponent'
import { SHOW_NOTIFICATION, showNotification } from 'ra-core'
import {
  ComponentAssociation,
  ComponentDependencies,
  NonModuleComponentWithQty,
  ParentComponents,
  stackingRules,
} from 'types/associatedComponents'
import { AnyComponentType } from 'types/components'
import { StudioComponentItem } from 'types/global'
import { ComponentTypes, NonModuleComponentTypes } from 'types/selectComponent'
import { StudioInverterType, StudioItem, StudioSystemType } from 'types/studio/items'
import { appendFilterComparisons, appendScopeComparison } from './designRulesConditionLog'
import { logDesignRules } from './designRulesDebug'
import { InverterSpecScope, SystemContext, ConditionCheckScope } from './types'

const computeModuleQuantity = (system: StudioSystemType, componentCode) =>
  system
    .getModuleTypes()
    .find((mg) => mg.code === componentCode)
    ?.getModuleQuantity() ?? 0

export const findComponentIn = (components: AnyComponentType[], code: string) => {
  return components.find((component) => upperCaseSafe(component.code) === upperCaseSafe(code))
}

const upperCaseSafe = (value: string | undefined) => {
  return value ? value.toUpperCase() : undefined
}

type InverterUserDataWithNestedSpecs = {
  inverters?: Array<Record<string, unknown>>
}

export function getInverterSpecScope(inv: StudioInverterType): InverterSpecScope {
  const componentSpecs = typeof inv.getComponentData === 'function' ? inv.getComponentData() : {}
  const primary = (inv.userData as InverterUserDataWithNestedSpecs | undefined)?.inverters?.[0] ?? {}

  const scope: InverterSpecScope = {
    ...(inv as unknown as InverterSpecScope),
    ...(primary as Partial<InverterSpecScope>),
    ...(componentSpecs as Partial<InverterSpecScope>),
  }

  if (inv.isHybridInverter?.() === true || isHybridInverter(scope)) {
    scope.hybrid = 'Y'
  }

  return scope
}

export function isHybridInverter(inverter: InverterSpecScope): boolean {
  const hybrid = inverter.hybrid as string | boolean | undefined
  return hybrid === 'Y' || hybrid === true
}

export function buildSystemInverterContext(system: StudioSystemType): {
  systemInverters: InverterSpecScope[]
} {
  const inverters = system.inverters() || []
  return {
    systemInverters: inverters.map(getInverterSpecScope),
  }
}

export function setSystemCustomData(
  context: SystemContext,
  prop: string | string[],
  value: unknown,
  key?: string
): void {
  if (!key) key = context.systemRuleKey
  const newSystemData = { ...(context.system.custom_data[key] || {}) }
  lodash.set(newSystemData, prop, value)
  const newCustomData = { ...context.system.custom_data, [key]: newSystemData }
  window.editor.execute(new window.SetValueCommand(context.system, 'custom_data', newCustomData))
}

// Returns null if any of the parents are not added to the system
// If componentToAdd is not provided, returns 0 when parents are present (useful for presence checks)
export function getRequiredQty(
  parentComponents: ParentComponents,
  componentToAdd: NonModuleComponentWithQty | undefined,
  system: StudioSystemType
): number | null {
  let qtyRequired: number = 0
  let useRelativeQtys: boolean = false
  for (const parentComponent of parentComponents) {
    let parentQtyInSystem: number = 0
    if (parentComponent.componentType === 'module') {
      if (!parentComponent.code) return system.moduleQuantity()
      parentQtyInSystem = computeModuleQuantity(system, parentComponent.code)
    } else {
      if (parentComponent.code) {
        parentQtyInSystem =
          getSystemItemsByCode(parentComponent.componentType, parentComponent.code, system)
            ?.map((component) => component['quantity'] || 1)
            .reduce((acc, cur) => acc + cur, 0) || 0
        // If no component code is specified, return total qty of the component type
      } else {
        if (parentComponent.componentType === 'inverter') {
          parentQtyInSystem = system.inverters().reduce((total, component) => total + (component['quantity'] || 1), 0)
        } else if (parentComponent.componentType === 'battery') {
          parentQtyInSystem = system.batteries().reduce((total, component) => total + (component['quantity'] || 1), 0)
        } else if (parentComponent.componentType === 'other') {
          parentQtyInSystem = system.others().reduce((total, component) => total + (component['quantity'] || 1), 0)
        }
      }
    }
    if (parentQtyInSystem === 0) return null
    // If componentToAdd is not provided, we're just checking presence, so skip quantity calculation
    if (!componentToAdd) continue
    // If the parentComponent qty is defined, we calculate the relative qty of componentToAdd required
    if (parentComponent.qty) {
      useRelativeQtys = true
      let qty = Math.floor(parentQtyInSystem / parentComponent.qty) * componentToAdd.qty
      if (!qtyRequired || qtyRequired > qty) qtyRequired = qty
    }
  }
  // If componentToAdd is not provided, return 0 to indicate parents are present
  if (!componentToAdd) {
    return 0
  }
  // If all parentComponents have no qty and are present in the system, use the static qty of componentToAdd
  if (!useRelativeQtys) {
    qtyRequired = componentToAdd.qty
  }
  return qtyRequired
}

export const getSystemItemsByCode = (type: ComponentTypes, code: string, system: StudioSystemType): StudioItem[] => {
  const components = getSystemItemsByType(type, system)
  return components.filter((component) => component.code === code)
}

const getSystemItemsByType = (type: ComponentTypes, system: StudioSystemType): StudioItem[] => {
  switch (type) {
    case 'other':
      return system.others()
    case 'inverter':
      return system.inverters()
    case 'battery':
      return system.batteries()
    case 'module':
      return system.getModules()
    case 'custom':
      return []
  }
}
export const getComponentQuantityInSystem = (system: StudioSystemType, type: ComponentTypes, code?: string): number => {
  if (type === 'module') {
    if (!code) return system.moduleQuantity()
    return computeModuleQuantity(system, code)
  }
  return getComponentInSystem(system, type, code).reduce((total, c) => (total += c['quantity'] || 1), 0)
}

export const getComponentInSystem = (
  system: StudioSystemType,
  type: NonModuleComponentTypes,
  code?: string
): StudioComponentItem[] => {
  let components: StudioComponentItem[]
  switch (type) {
    case 'inverter':
      components = system.inverters()
      break
    case 'battery':
      components = system.batteries()
      break
    case 'other':
      components = system.others()
      break
    default:
      throw new Error(`Unknown component type: ${type}`)
  }

  return !code ? components : components.filter((c) => c.code === code)
}

export const getActiveComponentsByType = (
  type: ComponentTypes | string,
  context: SystemContext
): AnyComponentType[] => {
  switch (type) {
    case 'other':
      return context.activeComponents.getOthers()
    case 'inverter':
      return context.activeComponents.getInverters()
    case 'battery':
      return context.activeComponents.getBatteries()
    case 'module':
      return context.activeComponents.getModules()
    default:
      throw new Error(`Unknown component type: ${type}`)
  }
}

export const getActiveComponentByCode = (
  type: ComponentTypes,
  code: string,
  context: SystemContext
): AnyComponentType | undefined => {
  const components = getActiveComponentsByType(type, context)
  return findComponentIn(components, code)
}

const addComponentToSystem = (
  component: AnyComponentType | undefined,
  type: ComponentTypes,
  quantity: number,
  systemUuid: string,
  authoredBy: string
) => {
  if (quantity === 0) {
    console.log(`[addComponentToSystem] Aborting - quantity is 0 for component ${component?.code}`)
    return
  }
  if (window.editor && component) {
    addComponent(type, systemUuid, { [type + 'Id']: component.id, quantity, authored_by: authoredBy })
  }
}

export const activateAndAddComponentToSystem = (
  code: string,
  type: ComponentTypes,
  quantity: number | (() => number),
  context: SystemContext,
  showNotifications: boolean,
  authoredBy: string,
  callback?: () => void
): Promise<void> => {
  return new Promise((resolve, reject) => {
    const component = getActiveComponentByCode(type, code, context)
    if (component) {
      const actualQuantity = typeof quantity === 'function' ? quantity() : quantity
      addComponentToSystem(component, type, actualQuantity, context.system.uuid, authoredBy)
      if (showNotifications)
        context.dispatch({ type: SHOW_NOTIFICATION, payload: { message: 'Component assigned to system' } })
      callback?.()
      resolve()
    } else if (context.role?.is_admin) {
      window.AccountHelper.activateComponents(
        [code],
        () => {
          const comp = getActiveComponentByCode(type, code, context)
          const actualQuantity = typeof quantity === 'function' ? quantity() : quantity
          addComponentToSystem(comp, type, actualQuantity, context.system.uuid, authoredBy)
          callback?.()
          resolve()
        },
        () => reject()
      )
      if (showNotifications) context.dispatch(showNotification('Component activated on account & assigned to system.'))
    } else {
      logDesignRules(`\tDesignRules: Skipping adding of un-activated component: code: ${code}`)
      reject()
    }
  })
}

// This activates the component if required
// Then adds, removes or adjusts the quantity of the component
// to match quantityAdjustment
export const activateAddAndAdjustQuantity = (
  code: string,
  type: NonModuleComponentTypes,
  context: SystemContext,
  quantityAdjustment: number,

  opts: { noNotifications?: boolean; noActivate?: boolean }
) => {
  const component = getActiveComponentByCode(type, code, context)
  if (!component) {
    // Component not activated on org
    if (opts.noActivate || quantityAdjustment <= 0 || !context.role?.is_admin) {
      logDesignRules(`\tDesignRules: Skipping adjustment of un-activated component: code: ${code}`)
      return
    } else {
      // Attempt to activate then add
      activateComponentForAccount(code, () => {
        const component = getActiveComponentByCode(type, code, context)
        if (!component) {
          console.error(`\tDesignRules: Failed to activate component: code: ${code}`)
          return
        }
        addRemoveOrAdjustComponentQuantity(component, type, context.system, quantityAdjustment)
      })

      if (!opts.noNotifications)
        context.dispatch(showNotification('Component activated on account & assigned to system.'))
    }
  } else {
    // Component is activated on org
    addRemoveOrAdjustComponentQuantity(component, type, context.system, quantityAdjustment)

    if (!opts.noNotifications)
      context.dispatch({
        type: SHOW_NOTIFICATION,
        payload: {
          message: 'Component assigned to system',
        },
      })
  }
}

const COMPONENT_TYPES_WITH_QUANTITY = ['other', 'battery']

// Then adds, removes or adjusts the quantity of the components added to the system
// to match quantityAdjustment
export const addRemoveOrAdjustComponentQuantity = (
  component: AnyComponentType,
  type: NonModuleComponentTypes,
  system: StudioSystemType,
  quantityAdjustment: number
) => {
  const existingItems = getComponentInSystem(system, type, component.code)
  // Currently we only adjust quantity for other components, although batteries do now support this
  while (
    quantityAdjustment !== 0 &&
    existingItems.length &&
    (quantityAdjustment < 0 || COMPONENT_TYPES_WITH_QUANTITY.includes(type))
  ) {
    for (const item in existingItems) {
      const existingQuantity = existingItems[item]['quantity'] || 1
      if (quantityAdjustment < 0) {
        if (existingQuantity <= -quantityAdjustment) {
          logDesignRules(
            `\tDesignRules: addRemoveOrAdjustComponentQuantity - remove component entirely: ${component.code}`
          )
          // Remove all of this item
          window.editor.deleteObject(existingItems[item])
        } else {
          logDesignRules(
            `\tDesignRules: addRemoveOrAdjustComponentQuantity - adjusting quantity [1]: ${component.code} (${quantityAdjustment})`
          )
          // Adjust quantity
          window.editor.execute(
            new window.SetValueCommand(
              existingItems[item],
              'quantity',
              existingQuantity + quantityAdjustment,
              window.Utils.generateCommandUUIDOrUseGlobal()
            )
          )
        }
        quantityAdjustment += existingQuantity
      } else {
        logDesignRules(
          `\tDesignRules: addRemoveOrAdjustComponentQuantity - adjusting quantity [2]: ${component.code} (${quantityAdjustment})`
        )
        window.editor.execute(
          new window.SetValueCommand(
            existingItems[item],
            'quantity',
            existingQuantity + quantityAdjustment,
            window.Utils.generateCommandUUIDOrUseGlobal()
          )
        )
        return
      }
    }
  }
  if (quantityAdjustment <= 0) return
  // Add remainder
  if (COMPONENT_TYPES_WITH_QUANTITY.includes(type)) {
    logDesignRules(
      `\tDesignRules: addRemoveOrAdjustComponentQuantity - add with quantity: ${component.code} (${quantityAdjustment})`
    )
    addComponent(type, system.uuid, { [type + 'Id']: component.id, quantity: quantityAdjustment })
  } else {
    logDesignRules(
      `\tDesignRules: addRemoveOrAdjustComponentQuantity - add components without quantity: ${component.code} (${quantityAdjustment})`
    )
    for (var i = 0; i < quantityAdjustment; i++) {
      addComponent(type, system.uuid, { [type + 'Id']: component.id })
    }
  }
}

const activateComponentForAccount = (code: string, callback: () => void) => {
  window.AccountHelper.activateComponents([code], callback)
}

export const ValidCheckOp = (op: string) => ops.includes(op as CheckOperator)
export type CheckScopeDef = {
  prop: string
  value: any
  op?: CheckOperator
}
export type CheckOperator = (typeof ops)[number]

export const checkScope = (scope: ConditionCheckScope, def: CheckScopeDef, context?: SystemContext): boolean => {
  const result = checkOp(scope, def.prop, def.op || 'eq', def.value)
  if (!result && context) {
    appendScopeComparison(context, scope, def)
  }
  return result
}

/**
 * Validates and evaluates a filter object against a scope.
 * Keys may include an operator via `__` suffix. Defaults to 'eq' when not provided.
 */
export function checkFilterAgainstScope(
  scope: ConditionCheckScope,
  filter: Record<string, any>,
  opts?: { debugLabel?: string; context?: SystemContext }
): boolean {
  if (!filter) return true
  const debugLabel = opts?.debugLabel || 'DesignRules'
  const context = opts?.context
  for (const key in filter) {
    const parts = key.split('__')
    if (parts.length !== 1 && parts.length !== 2) {
      logDesignRules(`\t${debugLabel}: Invalid filter key: `, key)
      return false
    }
    const [prop, op] = parts
    if (op && !ValidCheckOp(op)) {
      logDesignRules(`\t${debugLabel}: Invalid operator: `, op)
      return false
    }
    if (!checkScope(scope, { prop, op: (op as CheckOperator) || 'eq', value: filter[key] })) {
      if (context) {
        appendFilterComparisons(context, scope, filter)
      }
      return false
    }
  }
  return true
}

/**
 * Counts components of a specific type and code in the system
 */
export function countComponentsInSystem(type: string | ComponentTypes, code: string, context: SystemContext): number {
  // Cast string type to ComponentTypes if needed
  const componentType = type as ComponentTypes
  const components = getSystemItemsByCode(componentType, code, context.system)
  if (!components || components.length === 0) return 0

  // Sum up quantities if available, otherwise count objects
  return components.reduce((total, comp) => total + (comp.quantity ?? 1), 0)
}

export function calculateComponentMultiplier(
  component: ComponentAssociation,
  componentCounts: Map<string, number>,
  context: SystemContext,
  stackingRules?: stackingRules
): number {
  if (
    !stackingRules ||
    !stackingRules.targetComponentType ||
    !stackingRules.stackSize ||
    !Array.isArray(stackingRules.stackComponents)
  ) {
    return componentCounts.get('inverter') || 0
  }

  const {
    targetComponentType,
    targetComponentCode,
    stackSize,
    baseComponents = [],
    stackComponents,
    replicateBaseComponentsPerStack = false,
    stackingStrategy = 'even',
  } = stackingRules

  const isBaseComponent =
    baseComponents.length > 0
      ? baseComponents.some((c) => c.code === component.code)
      : !stackComponents.some((c) => c.code === component.code)

  const inverterCount = componentCounts.get('inverter') || 0

  const targetCount = targetComponentCode
    ? countComponentsInSystem(targetComponentType, targetComponentCode, context)
    : componentCounts.get(targetComponentType) || 0

  if (targetCount === 0 || inverterCount === 0) return 0

  const isStackComponent = stackComponents.some((c) => c.code === component.code)

  if (isStackComponent || (isBaseComponent && replicateBaseComponentsPerStack)) {
    let componentsPerInverter: number[]

    if (stackingStrategy === 'minimize') {
      componentsPerInverter = Array(inverterCount).fill(0)
      let remainingComponents = targetCount

      const baseComponentsPerInverter = Math.min(1, Math.floor(targetCount / inverterCount))
      componentsPerInverter = componentsPerInverter.fill(baseComponentsPerInverter)
      remainingComponents -= baseComponentsPerInverter * inverterCount

      let inverterIndex = 0
      while (remainingComponents > 0 && inverterIndex < inverterCount) {
        const currentStacks = Math.ceil(componentsPerInverter[inverterIndex] / stackSize)
        const capacityInCurrentStacks = currentStacks * stackSize - componentsPerInverter[inverterIndex]
        const componentsToAdd = Math.min(capacityInCurrentStacks, remainingComponents)
        componentsPerInverter[inverterIndex] += componentsToAdd
        remainingComponents -= componentsToAdd

        if (remainingComponents > 0) {
          inverterIndex++
        }
      }

      if (remainingComponents > 0) {
        inverterIndex = 0

        // Continue until we've distributed all components
        while (remainingComponents > 0) {
          // Add components to current inverter, up to a full stack
          const componentsToAdd = Math.min(stackSize, remainingComponents)
          componentsPerInverter[inverterIndex] += componentsToAdd
          remainingComponents -= componentsToAdd

          // If we've added a full stack, continue with same inverter
          // Otherwise, move to next inverter
          if (componentsToAdd === stackSize && remainingComponents > 0) {
            // Continue with same inverter for another full stack
            continue
          }

          inverterIndex = (inverterIndex + 1) % inverterCount
        }
      }
    } else {
      componentsPerInverter = Array(inverterCount).fill(Math.floor(targetCount / inverterCount))
      const remainder = targetCount % inverterCount
      for (let i = 0; i < remainder; i++) {
        componentsPerInverter[i]++
      }
    }

    const stacksPerInverter = componentsPerInverter.map((componentCount) => Math.ceil(componentCount / stackSize))

    logDesignRules(
      `ComponentMultiplier: ${component.code} (${
        isBaseComponent ? 'base' : 'stack'
      }) - ${stackingStrategy} distribution:`,
      `Components per inverter: ${componentsPerInverter.join(', ')}`,
      `Stacks per inverter: ${stacksPerInverter.join(', ')}`
    )

    if (isStackComponent) {
      const extraStacks = stacksPerInverter.reduce((sum, stacks) => sum + Math.max(0, stacks - 1), 0)
      return extraStacks
    }

    if (isBaseComponent && replicateBaseComponentsPerStack) {
      const totalBaseNeeded = stacksPerInverter.reduce((sum, stacks) => sum + stacks, 0)
      return totalBaseNeeded
    }
  }

  if (isBaseComponent) {
    return inverterCount
  }

  return inverterCount
}

export function getComponentCounts(dependencies: ComponentDependencies, context: SystemContext): Map<string, number> {
  const allParentRelationships = new Map<string, Set<string>>()

  dependencies.forEach((dependency) => {
    dependency.parentComponents?.forEach((parent) => {
      if (!parent.componentType) return

      const type = parent.componentType
      if (!allParentRelationships.has(type)) {
        allParentRelationships.set(type, new Set())
      }

      const parentWithCode = parent as { code?: string; componentType: string; oneOf?: any }

      if (parentWithCode.code) {
        allParentRelationships.get(type)?.add(parentWithCode.code)
      }
      // OneOf reference
      else if (parentWithCode.oneOf) {
        if (Array.isArray(parentWithCode.oneOf)) {
          parentWithCode.oneOf.forEach((item) => {
            if (item && typeof item === 'object' && 'code' in item && item.code) {
              allParentRelationships.get(type)?.add(item.code)
            }
          })
        } else if (typeof parentWithCode.oneOf === 'object') {
          Object.values(parentWithCode.oneOf).forEach((item: any) => {
            if (item && typeof item === 'object' && 'code' in item && item.code) {
              allParentRelationships.get(type)?.add(item.code)
            } else if (typeof item === 'string') {
              allParentRelationships.get(type)?.add(item)
            }
          })
        }
      }
    })
  })
  const componentCounts = new Map<string, number>()

  allParentRelationships.forEach((codes, type) => {
    let typeCount = 0
    codes.forEach((code) => {
      typeCount += countComponentsInSystem(type, code, context)
    })
    componentCounts.set(type, typeCount)
  })

  return componentCounts
}

export function normalizeStackingRules(rules: stackingRules): stackingRules {
  return {
    targetComponentType: rules.targetComponentType || 'battery',
    targetComponentCode: rules.targetComponentCode,
    stackSize: rules.stackSize || 3,
    baseComponents: Array.isArray(rules.baseComponents)
      ? rules.baseComponents
          .filter((c) => c && typeof c === 'object' && !!c.code && !!c.componentType)
          .map((c) => ({
            ...c,
            qty: c.qty || 1,
            lockComponent: c.lockComponent || false,
            mergeWithExisting: c.mergeWithExisting || false,
          }))
      : [],
    stackComponents: Array.isArray(rules.stackComponents)
      ? rules.stackComponents
          .filter((c) => c && typeof c === 'object' && !!c.code && !!c.componentType)
          .map((c) => ({
            ...c,
            qty: c.qty || 1,
            lockComponent: c.lockComponent || false,
            mergeWithExisting: c.mergeWithExisting || false,
          }))
      : [],
    replicateBaseComponentsPerStack: rules.replicateBaseComponentsPerStack ?? false,
    stackingStrategy:
      rules.stackingStrategy === 'minimize' || rules.stackingStrategy === 'even' ? rules.stackingStrategy : 'even',
  }
}

export function lockBatteryComponentIfNeeded(component: ComponentAssociation, batteryComponent: any): void {
  if (component.lockComponent && component.componentType === 'battery' && batteryComponent.type === 'OsBattery') {
    if (!batteryComponent.componentLocked) {
      window.editor.execute(
        new window.SetValueCommand(
          batteryComponent,
          'componentLocked',
          true,
          window.Utils.generateCommandUUIDOrUseGlobal()
        )
      )
    }
  }
}

export function lockStudioComponentByCode(
  componentType: ComponentTypes,
  code: string,
  system: StudioSystemType,
  locked: boolean
): void {
  type LockableStudioComponent = StudioItem & { componentLocked?: boolean }
  const components = getSystemItemsByCode(componentType, code, system) as LockableStudioComponent[]
  components.forEach((studioComponent) => {
    if (studioComponent.componentLocked === locked) {
      return
    }
    window.editor.execute(
      new window.SetValueCommand(
        studioComponent,
        'componentLocked',
        locked,
        window.Utils.generateCommandUUIDOrUseGlobal()
      )
    )
  })
}

export function saveSystemCustomData(system: StudioSystemType): void {
  window.editor.execute(new window.SetValueCommand(system, 'custom_data', system.custom_data))
}
